import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  hasCategoryLabel,
  hasFuelLabels,
  sumEuroPercentages,
  sumFuelPercentages,
} from "src/utils/fleet-percentage.utils";

const INTEGER_MESSAGE = "Must be a whole number";

const euroSchema = Yup.object({
  euro: Yup.string(),
  euroTranslation: Yup.string(),
  euroPercentage: Yup.number().integer(INTEGER_MESSAGE).min(0).max(100).required(),
});

export function useFleetPercentageSchema() {
  const { t } = useTranslation();
  const euroMessage = t("label.fleetPercentageEurosMustSumTo100");
  const fuelMessage = t("label.fleetPercentageFuelsMustSumTo100");

  const fuelSchema = Yup.object({
    fuel: Yup.string(),
    fuelTranslation: Yup.string(),
    fuelPercentage: Yup.number().integer(INTEGER_MESSAGE).min(0).max(100).required(),
    euros: Yup.array()
      .of(euroSchema)
      .test("euro-sum-100", euroMessage, function (euros) {
        const valid = sumEuroPercentages(euros) === 100;
        if (valid) return true;
        const parent = this.parent;
        const parts = hasFuelLabels(parent) ? [parent.categoryTranslation, parent.fuelTranslation].filter(Boolean) : [];
        const prefix = parts.length ? `${parts.join(" / ")}: ` : "";
        return this.createError({ message: prefix + euroMessage });
      }),
  });

  const categorySchema = Yup.object({
    category: Yup.string(),
    categoryTranslation: Yup.string(),
    categoryPercentage: Yup.number().integer(INTEGER_MESSAGE).min(0).max(100).required(),
    fuels: Yup.array()
      .of(fuelSchema)
      .test("fuel-sum-100", fuelMessage, function (fuels) {
        const valid = sumFuelPercentages(fuels) === 100;
        if (valid) return true;
        const parent = this.parent;
        const prefix = hasCategoryLabel(parent) && parent.categoryTranslation ? `${parent.categoryTranslation}: ` : "";
        // Use a sibling path so it doesn't conflict with nested fuels[].euros errors.
        // Formik's errors object can't hold both "fuels"=string and "fuels.0.euros"=string.
        const pathParts = this.path.split(".");
        pathParts[pathParts.length - 1] = "fuelsSummaryError";
        return this.createError({
          message: prefix + fuelMessage,
          path: pathParts.join("."),
        });
      }),
  });

  return Yup.object({
    categories: Yup.array().of(categorySchema),
  });
}
